import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useAuth } from '@/contexts/AuthContext'
import { Bell, User, LogOut, Settings, Zap, Activity, Wifi, Music, Palette, Sparkles } from 'lucide-react'
import CosmicButton from '../ui/CosmicButton'

const Header: React.FC = () => {
  const { user, logout } = useAuth()
  const [showUserMenu, setShowUserMenu] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout error:', error)
    }
  }

  return (
    <motion.header 
      className="cosmic-glass border-b border-stellar-blue/20 px-6 py-4 relative z-50"
      initial={{ y: -50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      style={{ 
        position: 'relative',
        zIndex: 50
      }}
    >
      {/* Top border accent */}
      <div className="absolute top-0 left-0 w-full h-1 bg-stellar-gradient" />
      
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-6">
          <motion.h2 
            className="text-xl font-display font-semibold text-white glow-text"
            animate={{ textShadow: ["0 0 10px var(--stellar-blue)", "0 0 20px var(--stellar-blue)", "0 0 10px var(--stellar-blue)"] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            COSMIC HUB
          </motion.h2>
          
          {/* System Indicators */}
          <div className="flex items-center space-x-4">
            <motion.div 
              className="flex items-center gap-2 px-3 py-1 bg-cosmic-dark/50 rounded-lg border border-stellar-green/30"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Music className="w-4 h-4 text-stellar-green" />
              <span className="text-xs font-mono text-stellar-green uppercase tracking-wider">
                Live
              </span>
            </motion.div>
            
            <motion.div 
              className="flex items-center gap-2 px-3 py-1 bg-cosmic-dark/50 rounded-lg border border-stellar-blue/30"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              <Palette className="w-4 h-4 text-stellar-blue" />
              <span className="text-xs font-mono text-stellar-blue uppercase tracking-wider">
                Creative
              </span>
            </motion.div>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Notifications */}
          <motion.button 
            className="p-2 text-stellar-cyan hover:text-stellar-blue transition-colors relative"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <Bell className="h-5 w-5" />
            {/* Notification indicator */}
            <motion.div
              className="absolute -top-1 -right-1 w-3 h-3 bg-danger-red rounded-full"
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            />
          </motion.button>

          {/* User Menu */}
          <div className="relative">
            <motion.button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 rounded-lg cosmic-glass hover:border-stellar-blue/50 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div 
                className="w-8 h-8 bg-stellar-gradient rounded-full flex items-center justify-center border-2 border-stellar-blue/50"
                whileHover={{ rotate: 360 }}
                transition={{ duration: 0.5 }}
              >
                <span className="text-white text-sm font-display font-medium">
                  {user?.email?.charAt(0).toUpperCase()}
                </span>
              </motion.div>
              <span className="text-sm font-mono text-stellar-cyan">{user?.email}</span>
              <motion.div
                animate={{ rotate: showUserMenu ? 180 : 0 }}
                transition={{ duration: 0.3 }}
              >
                <Sparkles className="w-4 h-4 text-stellar-blue" />
              </motion.div>
            </motion.button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div 
                  className="absolute right-0 mt-2 w-64 cosmic-glass rounded-lg border border-stellar-blue/30 py-2 z-[9999]"
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  style={{ 
                    position: 'absolute',
                    zIndex: 9999,
                    pointerEvents: 'auto'
                  }}
                >
                  {/* User Info */}
                  <div className="px-4 py-3 border-b border-stellar-blue/20">
                    <p className="text-sm font-mono text-white">{user?.email}</p>
                    <p className="text-xs font-mono text-stellar-cyan uppercase tracking-wider">
                      {user?.role}
                    </p>
                  </div>
                  
                  {/* Menu Items */}
                  <div className="py-2">
                    <motion.button 
                      className="w-full text-left px-4 py-2 text-sm text-stellar-cyan hover:bg-stellar-blue/10 hover:text-stellar-blue flex items-center transition-colors duration-200"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <User className="mr-3 h-4 w-4" />
                      Profile
                    </motion.button>
                    
                    <motion.button 
                      className="w-full text-left px-4 py-2 text-sm text-stellar-cyan hover:bg-stellar-blue/10 hover:text-stellar-blue flex items-center transition-colors duration-200"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Settings className="mr-3 h-4 w-4" />
                      Settings
                    </motion.button>
                    
                    <motion.button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-danger-red hover:bg-danger-red/10 flex items-center transition-colors duration-200"
                      whileHover={{ x: 5 }}
                      transition={{ duration: 0.2 }}
                    >
                      <LogOut className="mr-3 h-4 w-4" />
                      Logout
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Close user menu when clicking outside */}
      <AnimatePresence>
        {showUserMenu && (
          <motion.div
            className="fixed inset-0 z-[9998]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowUserMenu(false)}
            style={{ 
              position: 'fixed',
              zIndex: 9998,
              pointerEvents: 'auto'
            }}
          />
        )}
      </AnimatePresence>
    </motion.header>
  )
}

export default Header 